import { ChildPayButtonProps } from './types';
import { usePaymentCoordinator } from '../../../hooks/payment/usePaymentCoordinator';
import { PaymentGateway, PaymentMethod } from '../../../services/exports/Constants';
import { CheckoutFunction } from '../../../hooks/payment/useOrderCheckout';
import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes } from '../../../services/exports/Interfaces';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import ApplePayIcon from '../../../assets/icons/payment-methods/ApplePayIcon';
import classnames from 'classnames';
import GooglePayButton from './GooglePayButton';
import paymentLog from '../../../services/helpers/PaymentLogger';

export default function AdyenPayButton({
  title,
  loading,
  disabled,
  silentlyDisabled,
  onClick,
  className,
}: ChildPayButtonProps) {
  const { paymentGatewaySDK } = usePaymentCoordinator(PaymentGateway.Adyen);
  const { paymentElement } = paymentGatewaySDK;
  const { selectedPaymentMethod } = usePaymentFlow();

  function onSubmit() {
    // paymentElement.submit() throws if the Drop-in has no active/valid payment method
    // (e.g. a half-mounted Drop-in), which otherwise becomes an unhandled rejection and
    // crashes the payment page. Guard + log via paymentLog instead of letting it throw.
    try {
      paymentElement?.submit();
    } catch (error) {
      paymentLog.error('adyen', 'sdk_error', {
        payment_method: selectedPaymentMethod,
        message: (error as Error)?.message,
      });
    }
  }

  const _onClick = () => onClick(() => null, null, onSubmit as unknown as CheckoutFunction);

  if (selectedPaymentMethod === PaymentMethod.GooglePay) {
    return (
      <GooglePayButton
        onClick={_onClick}
        loading={loading}
        disabled={disabled}
        silentlyDisabled={silentlyDisabled || !paymentElement}
        className={className}
      />
    );
  }

  const buttonType =
    {
      [PaymentMethod.ApplePay]: ButtonTypes.secondary,
    }[selectedPaymentMethod] ?? ButtonTypes.primary;

  const customIcon =
    {
      [PaymentMethod.ApplePay]: <ApplePayIcon color="white" className="my-auto" />,
    }[selectedPaymentMethod] ?? null;

  return (
    <CustomButton
      buttonType={buttonType}
      title={title}
      disabled={disabled}
      silentlyDisabled={silentlyDisabled || !paymentElement}
      onClick={_onClick}
      loading={loading}
      showArrow
      customIcon={customIcon}
      className={classnames(className, {
        'flex-row-reverse gap-sm': selectedPaymentMethod === PaymentMethod.ApplePay,
      })}
    />
  );
}
